import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Download } from 'lucide-react';
import Layout from '../layout/Layout';
import api from '../../lib/api';

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];
const DAYS_FR = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const dateInRange = (date, start, end) => {
  const d = date.getTime();
  return d >= new Date(start).getTime() && d <= new Date(end).getTime();
};

const formatDateFR = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const MonthCard = ({ year, month, vacations, holidays, noClassDays }) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const totalDays = lastDay.getDate();
  let startWeekday = firstDay.getDay() - 1;
  if (startWeekday < 0) startWeekday = 6;

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="bg-white rounded-xl shadow-md p-4 border border-slate-100">
      <h3 className="text-base font-bold text-[#2C3C65] mb-3 text-center">
        {MONTHS_FR[month]} <span className="text-[#6777A1] font-normal">{year}</span>
      </h3>
      <div className="grid grid-cols-7 gap-1 text-center">
        {DAYS_FR.map((d, i) => (
          <div key={i} className="text-[10px] font-semibold text-[#6777A1] uppercase pb-1">{d}</div>
        ))}
        {cells.map((cell, idx) => {
          if (!cell) return <div key={idx} className="h-7" />;
          const day = cell.getDay();
          const isWeekend = day === 0 || day === 6;
          const isVac = vacations.some((v) => dateInRange(cell, v.start, v.end));
          const ds = cell.toISOString().slice(0, 10);
          const isPub = holidays.some((h) => h.date === ds);
          const isSchoolOff = noClassDays.some((h) => h.date === ds && !isPub);
          const pubName = holidays.find((h) => h.date === ds)?.name;
          const offName = noClassDays.find((h) => h.date === ds)?.name;
          let cls = 'h-7 flex items-center justify-center text-xs rounded transition-colors ';
          if (isVac) cls += 'bg-rose-500 text-white font-bold';
          else if (isPub) cls += 'bg-rose-100 text-rose-700 font-bold ring-1 ring-rose-300';
          else if (isSchoolOff) cls += 'bg-amber-200 text-amber-900 font-bold ring-1 ring-amber-400';
          else if (isWeekend) cls += 'text-slate-400';
          else cls += 'text-[#2C3C65] hover:bg-blue-50';
          return (
            <div key={idx} className={cls} title={pubName || offName || ''}>
              {cell.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const buildMonths = (year) => {
  const [y1] = year.split('-').map(Number);
  return Array.from({ length: 12 }, (_, i) => {
    const m = (8 + i) % 12;
    const yr = i < 4 ? y1 : y1 + 1;
    return { year: yr, month: m };
  });
};

const CalendrierPage = () => {
  const [data, setData] = useState({
    school_calendar_year: '2026-2027',
    school_calendar_pdf_url: '',
    school_vacations: [],
    school_no_class_days: [],
  });

  useEffect(() => {
    api.get('/settings/site-info')
      .then((r) => setData((d) => ({ ...d, ...r.data })))
      .catch(() => {});
  }, []);

  // Split no_class_days into "true public holidays" (full year recurring) and "school-specific"
  // For simplicity, treat all entries from school_no_class_days as public holidays
  // and add an optional separate list later. We rely on visual distinction via colors only when needed.
  const months = buildMonths(data.school_calendar_year || '2026-2027');
  const vacations = data.school_vacations || [];
  const holidays = data.school_no_class_days || [];

  return (
    <Layout>
      <div className="min-h-screen bg-[#F5F7FF] py-12" data-testid="calendrier-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-blue-100 border border-blue-200">
              <Calendar className="h-4 w-4 text-[#285BBB]" />
              <span className="text-[#285BBB] text-xs uppercase tracking-widest font-semibold">Année {data.school_calendar_year}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2C3C65] mb-4">
              Calendrier scolaire <span className="text-[#285BBB]">{data.school_calendar_year}</span>
            </h1>
            <p className="text-lg text-[#6777A1] max-w-2xl mx-auto">
              Retrouvez les périodes de vacances scolaires (Zone B - Aix-Marseille) et les jours fériés.
              Les cours suivent ce calendrier.
            </p>
          </motion.div>

          {/* Calendrier officiel (PDF embarqué) */}
          {data.school_calendar_pdf_url && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-10"
              data-testid="calendrier-pdf-preview"
            >
              <div className="bg-gradient-to-r from-[#285BBB] to-[#2C3C65] px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-white">
                  <Calendar className="h-5 w-5" />
                  <span className="font-semibold">Calendrier officiel {data.school_calendar_year}</span>
                </div>
                <a
                  href={data.school_calendar_pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download="calendrier-2026-2027.pdf"
                  className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                  data-testid="calendrier-pdf-download"
                >
                  <Download className="h-3.5 w-3.5" />
                  Télécharger
                </a>
              </div>
              <iframe
                src={`${data.school_calendar_pdf_url}#toolbar=0&navpanes=0&view=FitH`}
                title="Calendrier scolaire officiel"
                className="w-full h-[820px] bg-white"
                data-testid="calendrier-pdf-iframe"
              />
            </motion.div>
          )}

          <div className="bg-white rounded-2xl shadow-md p-4 mb-8 flex items-center justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-5 h-5 bg-rose-500 rounded" />
              <span className="text-[#2C3C65]">Vacances scolaires</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-5 h-5 bg-rose-100 ring-1 ring-rose-300 rounded" />
              <span className="text-[#2C3C65]">Jour férié / sans cours</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="w-5 h-5 bg-blue-50 rounded" />
              <span className="text-[#2C3C65]">Jour de cours possible</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-12">
            {months.map((m) => (
              <MonthCard
                key={`${m.year}-${m.month}`}
                year={m.year}
                month={m.month}
                vacations={vacations}
                holidays={holidays}
                noClassDays={[]}
              />
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-lg font-bold text-[#2C3C65] mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-rose-500 rounded-full" />
                Vacances scolaires
              </h2>
              <ul className="space-y-3">
                {vacations.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                    <div className="w-1.5 h-1.5 bg-rose-500 rounded-full mt-2 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-[#2C3C65]">{h.name}</p>
                      <p className="text-sm text-[#6777A1]">Du {formatDateFR(h.start)} au {formatDateFR(h.end)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-lg font-bold text-[#2C3C65] mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-rose-300 rounded-full" />
                Jours fériés
              </h2>
              <ul className="space-y-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                {holidays.map((h, i) => (
                  <li key={i} className="flex items-center justify-between text-sm py-1">
                    <span className="text-[#2C3C65]">{h.name}</span>
                    <span className="text-[#6777A1] text-xs">{new Date(h.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {data.school_calendar_pdf_url && (
            <div className="text-center mt-8">
              <a
                href={data.school_calendar_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#285BBB] hover:bg-[#1e47a0] text-white font-semibold rounded-xl transition-colors"
                data-testid="download-calendrier"
              >
                <Download className="h-4 w-4" />
                Télécharger le calendrier officiel (PDF)
              </a>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CalendrierPage;
